import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Shield,
  Sparkles,
  Copy,
  Check,
  RefreshCw,
  ArrowLeft,
  FileText,
  FileDiff,
  Download,
  Send,
  X,
  DollarSign,
  Clock,
  AlertTriangle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_TYPES = [
  { value: "web", label: "Web Design/Development" },
  { value: "branding", label: "Branding & Identity" },
  { value: "consulting", label: "Consulting" },
  { value: "seo", label: "SEO & Marketing" },
  { value: "copywriting", label: "Copywriting" }
];

const OUTPUT_TYPES = [
  { value: "revision_policy", label: "Revision Policy" },
  { value: "scope", label: "Scope Definition" }
];

export default function ScopeGuard({ user, usage, refreshUsage, tutorial }) {
  const navigate = useNavigate();
  const isOutOfCredits =
    user?.tier === "free" &&
    !usage?.is_demo &&
    typeof usage?.llm_credits_remaining === "number" &&
    usage.llm_credits_remaining <= 0;

  // v0.2 Plan 06-03: tutorial pre-fill matrix keyed on user.primary_use_case
  // (passed via tutorial.useCase from App.js). All paths default to revision_policy
  // except brief_changes / pricing_pushback which map to scope (Scope Definition).
  // Verified against actual SERVICE_TYPES + OUTPUT_TYPES enum surface.
  const TUTORIAL_PREFILL = {
    scope_creep:      { serviceType: "branding",   outputType: "revision_policy", rounds: 2 },
    late_pay:         { serviceType: "consulting", outputType: "revision_policy", rounds: 2 },
    brief_changes:    { serviceType: "branding",   outputType: "scope",           rounds: 2 },
    pricing_pushback: { serviceType: "branding",   outputType: "scope",           rounds: 2 },
    exploring:        { serviceType: "branding",   outputType: "revision_policy", rounds: 2 },
  };
  const tutorialPrefill = tutorial
    ? TUTORIAL_PREFILL[tutorial.useCase] || TUTORIAL_PREFILL.exploring
    : null;
  const [serviceType, setServiceType] = useState(tutorialPrefill ? tutorialPrefill.serviceType : "web");
  const [outputType, setOutputType] = useState(tutorialPrefill ? tutorialPrefill.outputType : "revision_policy");
  const [rounds, setRounds] = useState(tutorialPrefill ? tutorialPrefill.rounds : 2);
  const [timelineDays, setTimelineDays] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Change Order Modal State
  const [showChangeOrderModal, setShowChangeOrderModal] = useState(false);
  const [projects, setProjects] = useState([]);
  const [changeOrderForm, setChangeOrderForm] = useState({
    project_id: '',
    reason: '',
    scope_diff: '',
    pricing_adjustment: 0,
    timeline_adjustment_days: 0
  });
  const [generatingChangeOrder, setGeneratingChangeOrder] = useState(false);
  const [generatedChangeOrder, setGeneratedChangeOrder] = useState(null);

  // Load projects for change order dropdown
  useEffect(() => {
    loadProjects();
  }, []);

  // v0.2 Plan 05-04: tutorial auto-fire on step 2.
  // 1.5s after step 2 mounts, fire handleGenerate so the designer sees a real
  // LLM response without lifting a finger. Tutorial step 2 overlay above the form
  // is the "race risk" mentioned in the plan checkpoint — we mitigate by relying
  // on tutorial.step as the gate (won't fire on subsequent renders).
  useEffect(() => {
    if (!tutorial || tutorial.step !== 2) return;
    if (markdown || loading) return;
    const t = setTimeout(() => {
      handleGenerate();
    }, 1500);
    return () => clearTimeout(t);
    // handleGenerate is intentionally omitted — deps locked to tutorial state so
    // the timer fires exactly once per step-2 entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutorial?.step]);

  // v0.2 Plan 05-04: when markdown lands during tutorial step 2, advance to step 3.
  // refreshUsage already fired inside handleGenerate, so credit pill updates with
  // the advance to step 3 — that's the aha visual payoff.
  useEffect(() => {
    if (!tutorial || tutorial.step !== 2) return;
    if (!markdown || loading) return;
    tutorial.onAdvance?.(3);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markdown, loading, tutorial?.step]);

  const loadProjects = async () => {
    try {
      const res = await api.get("/projects");
      setProjects(res.data.projects || []);
    } catch (e) {
      console.error("Failed to load projects:", e);
    }
  };

  const handleGenerate = async () => {
    if (isOutOfCredits) {
      navigate("/billing");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/scope-guard/generate", {
        service_type: serviceType,
        output: outputType,
        options: {
          rounds: rounds,
          timeline_days: timelineDays ? parseInt(timelineDays) : undefined
        }
      });

      setMarkdown(res.data.markdown || "");
      toast.success(`${outputType === "revision_policy" ? "Revision Policy" : "Scope"} generated!`);
      refreshUsage?.();
    } catch (e) {
      console.error("Generate error:", e);
      toast.error(e.response?.data?.detail || "Failed to generate");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(markdown);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  // Pre-fill change order from generated scope content
  const handleCreateChangeOrder = () => {
    if (markdown) {
      setChangeOrderForm(prev => ({
        ...prev,
        scope_diff: markdown,
        reason: "Scope modification based on generated scope definition"
      }));
    }
    setShowChangeOrderModal(true);
  };

  const handleGenerateChangeOrder = async () => {
    if (!changeOrderForm.project_id) {
      toast.error("Please select a project");
      return;
    }
    
    setGeneratingChangeOrder(true);
    try {
      const res = await api.post(`/change-orders/projects/${changeOrderForm.project_id}`, {
        reason: changeOrderForm.reason,
        scope_diff: changeOrderForm.scope_diff,
        pricing_adjustment: changeOrderForm.pricing_adjustment,
        timeline_adjustment_days: changeOrderForm.timeline_adjustment_days
      });
      
      setGeneratedChangeOrder(res.data);
      toast.success("Change order generated!");
    } catch (e) {
      console.error("Change order error:", e);
      toast.error(e.response?.data?.detail || "Failed to generate change order");
    } finally {
      setGeneratingChangeOrder(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!generatedChangeOrder?._id) return;
    
    try {
      const response = await fetch(`${API_URL}/api/change-orders/${generatedChangeOrder._id}/export-pdf`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Change_Order_${generatedChangeOrder._id.slice(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("PDF downloaded!");
      } else {
        toast.error("Failed to download PDF");
      }
    } catch (e) {
      toast.error("Failed to download PDF");
    }
  };

  const handleSendChangeOrder = () => {
    if (generatedChangeOrder?._id) {
      navigate(`/change-orders?id=${generatedChangeOrder._id}&action=send`);
    }
  };

  const resetChangeOrderModal = () => {
    setShowChangeOrderModal(false);
    setGeneratedChangeOrder(null);
    setChangeOrderForm({
      project_id: '',
      reason: '',
      scope_diff: '',
      pricing_adjustment: 0,
      timeline_adjustment_days: 0
    });
  };

  return (
    <div className="space-y-6" data-testid="scope-guard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/projects"
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-indigo-400" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-indigo-500">Scope Guard</h1>
            <p className="text-indigo-400">
              Generate professional scope definitions and revision policies
            </p>
          </div>
        </div>
        
        {/* Scope Creep Action Button */}
        <Button
          onClick={handleCreateChangeOrder}
          className="bg-amber-500 hover:bg-amber-600 text-white"
          data-testid="create-change-order-btn"
        >
          <FileDiff className="w-4 h-4 mr-2" />
          Create Change Order
        </Button>
      </div>

      {/* Scope Creep Alert Banner */}
      <Card className="bg-amber-500/10 border-amber-500/30">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-500/20 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-amber-300">Experiencing Scope Creep?</h3>
              <p className="text-sm text-amber-200/70">
                Generate a professional change order to document new requests, adjust pricing, and protect your project timeline.
              </p>
            </div>
            <Button
              onClick={handleCreateChangeOrder}
              variant="outline"
              className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
            >
              Generate Change Order
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Configuration */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500 flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-400" />
            Configuration
          </CardTitle>
          <CardDescription className="text-indigo-400">
            Customize your scope or revision policy based on your service type
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Service Type */}
            <div>
              <label className="block text-sm text-indigo-400 mb-2">Service Type</label>
              <Select value={serviceType} onValueChange={setServiceType}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {SERVICE_TYPES.map((s) => (
                    <SelectItem key={s.value} value={s.value} className="text-slate-400">
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Output Type */}
            <div>
              <label className="block text-sm text-indigo-400 mb-2">Output Type</label>
              <Select value={outputType} onValueChange={setOutputType}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {OUTPUT_TYPES.map((o) => (
                    <SelectItem key={o.value} value={o.value} className="text-slate-400">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Revision Rounds */}
            <div>
              <label className="block text-sm text-indigo-400 mb-2">Revision Rounds</label>
              <Select value={rounds.toString()} onValueChange={(v) => setRounds(parseInt(v))}>
                <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <SelectItem key={n} value={n.toString()} className="text-slate-400">
                      {n} round{n !== 1 ? "s" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Timeline (optional) */}
            <div>
              <label className="block text-sm text-indigo-400 mb-2">Timeline (days)</label>
              <input
                type="number"
                value={timelineDays}
                onChange={(e) => setTimelineDays(e.target.value)}
                placeholder="Optional"
                className="w-full px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-indigo-500 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
              />
            </div>
          </div>
          
          <button
            onClick={handleGenerate}
            disabled={loading}
            className={`mt-6 flex items-center gap-2 px-6 py-3 rounded-xl transition-colors text-white disabled:bg-slate-600 ${
              isOutOfCredits
                ? "bg-[#B23A48] hover:bg-[#9c2e3b]"
                : "bg-indigo-500 hover:bg-indigo-600"
            }`}
            data-testid="generate-scope-btn"
          >
            {loading ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Sparkles className="w-5 h-5" />
            )}
            {isOutOfCredits
              ? "Upgrade to continue"
              : `Generate ${outputType === "revision_policy" ? "Revision Policy" : "Scope Definition"}`}
          </button>
        </CardContent>
      </Card>

      {/* Output */}
      {markdown && (
        <Card className="bg-slate-800/50 border-slate-700/50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-indigo-500 flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-400" />
                Generated Content
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleCreateChangeOrder}
                  variant="outline"
                  className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
                  size="sm"
                >
                  <FileDiff className="w-4 h-4 mr-1" />
                  Use as Change Order
                </Button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white px-3 py-2 rounded-lg transition-colors"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                  Copy
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              className="min-h-[400px] font-mono text-sm bg-slate-900/50 border-slate-600 text-slate-300"
              data-testid="scope-content"
            />
          </CardContent>
        </Card>
      )}

      {/* Tips */}
      <Card className="bg-slate-800/50 border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-indigo-500 text-sm">Tips for Using Scope Guard</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-indigo-400">
            <li>• <strong>Revision Policy:</strong> Include in your proposals and contracts to set clear expectations</li>
            <li>• <strong>Scope Definition:</strong> Use at project kickoff to document what's included and excluded</li>
            <li>• <strong>Change Orders:</strong> When scope creep occurs, generate a formal change order to protect your timeline and get paid for extra work</li>
            <li>• Consider including this in your Client Portal for transparency</li>
          </ul>
        </CardContent>
      </Card>

      {/* Change Order Modal */}
      <Dialog open={showChangeOrderModal} onOpenChange={resetChangeOrderModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileDiff className="w-5 h-5 text-amber-400" />
              {generatedChangeOrder ? "Change Order Generated" : "Create Change Order"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {generatedChangeOrder 
                ? "Your change order is ready. Download the PDF or send it to your client."
                : "Document scope changes and generate a professional change order."
              }
            </DialogDescription>
          </DialogHeader>

          {!generatedChangeOrder ? (
            <div className="space-y-4 py-4">
              {/* Project Selection */}
              <div>
                <Label className="text-slate-300">Project *</Label>
                <Select 
                  value={changeOrderForm.project_id} 
                  onValueChange={(v) => setChangeOrderForm(prev => ({ ...prev, project_id: v }))}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                    <SelectValue placeholder="Select a project" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {projects.map((p) => (
                      <SelectItem key={p.id || p._id} value={p.id || p._id} className="text-slate-300">
                        {p.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Reason */}
              <div>
                <Label className="text-slate-300">Reason for Change *</Label>
                <Input
                  value={changeOrderForm.reason}
                  onChange={(e) => setChangeOrderForm(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="e.g., Client requested additional features"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>

              {/* Scope Changes */}
              <div>
                <Label className="text-slate-300">Scope Changes *</Label>
                <Textarea
                  value={changeOrderForm.scope_diff}
                  onChange={(e) => setChangeOrderForm(prev => ({ ...prev, scope_diff: e.target.value }))}
                  placeholder="Describe what's being added, changed, or removed from the original scope..."
                  className="bg-slate-800 border-slate-700 text-white mt-1 min-h-[120px]"
                />
              </div>

              {/* Pricing & Timeline */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300 flex items-center gap-1">
                    <DollarSign className="w-4 h-4" />
                    Price Adjustment
                  </Label>
                  <Input
                    type="number"
                    value={changeOrderForm.pricing_adjustment}
                    onChange={(e) => setChangeOrderForm(prev => ({ ...prev, pricing_adjustment: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Days Added
                  </Label>
                  <Input
                    type="number"
                    value={changeOrderForm.timeline_adjustment_days}
                    onChange={(e) => setChangeOrderForm(prev => ({ ...prev, timeline_adjustment_days: parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                    className="bg-slate-800 border-slate-700 text-white mt-1"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* Success State */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-500/20 rounded-full">
                    <Check className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-300">Change Order Created</h4>
                    <p className="text-sm text-green-200/70">
                      Price adjustment: +${changeOrderForm.pricing_adjustment.toLocaleString()} | 
                      Timeline: +{changeOrderForm.timeline_adjustment_days} days
                    </p>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div>
                <Label className="text-slate-300 mb-2 block">Preview</Label>
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 max-h-[200px] overflow-y-auto">
                  <pre className="text-sm text-slate-300 whitespace-pre-wrap font-mono">
                    {generatedChangeOrder.markdown?.slice(0, 500)}...
                  </pre>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            {!generatedChangeOrder ? (
              <>
                <Button variant="outline" onClick={resetChangeOrderModal} className="border-slate-600 text-slate-300">
                  Cancel
                </Button>
                <Button 
                  onClick={handleGenerateChangeOrder}
                  disabled={generatingChangeOrder || !changeOrderForm.project_id}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  {generatingChangeOrder ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Change Order
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={resetChangeOrderModal} className="border-slate-600 text-slate-300">
                  Close
                </Button>
                <Button 
                  onClick={handleDownloadPDF}
                  variant="outline"
                  className="border-indigo-500 text-indigo-400 hover:bg-indigo-500/20"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                <Button 
                  onClick={handleSendChangeOrder}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send to Client
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
